import React, { useRef, useState } from "react";
import { ArrowBackIos } from "@mui/icons-material";
import { useCreateOrganization } from "../apiCalls/userApiCalls";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function CreateOrganization() {
  const nameInputElement = useRef();
  const instanceUrlInputElement = useRef();
  const securityTokenInputElement = useRef();
  const usernameInputElement = useRef();
  const passwordInputElement = useRef();
  const otThresholdInputElement = useRef();
  const otMultiplierInputElement = useRef();
  const otMethodInputElement = useRef();
  const [isSalesforceSyncEnabled, setIsSalesforceSyncEnabled] = useState(false);

  const { currentUser } = useSelector((state) => state.userSlice) || null;
  const token = currentUser.token;
  const navigate = useNavigate();

  const {
    mutate: createOrganizationMutate,
    isLoading: isCreateOrganizationLoading,
    isError: isCreateOrganizationError,
    error: createOrganizationError,
  } = useCreateOrganization();

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = {
      token: token,
      name: nameInputElement.current?.value,
      salesforceInstanceUrl: instanceUrlInputElement.current?.value,
      salesforceUserSecurityToken: securityTokenInputElement.current?.value,
      salesforceUsername: usernameInputElement.current?.value,
      salesforceUserPassword: passwordInputElement.current?.value,
      salesforceSyncEnabled: isSalesforceSyncEnabled,
      overtimeThreshold: otThresholdInputElement.current?.value,
      overtimeMultiplier: otMultiplierInputElement.current?.value,
      overtimeCalculationMethod: otMethodInputElement.current?.value,
    };

    createOrganizationMutate(data, {
      onSuccess: (data) => {
        console.log("Organization created successfully:", data);
        const organizationId = data?.data?.data?._id;
        if (organizationId) {
          navigate(`/organizationDetails/${organizationId}`);
        } else {
          console.error("Organization ID is undefined:", data);
        }
      },
    });
  };

  const handleSalesforceSyncChange = (e) => {
    setIsSalesforceSyncEnabled(e.target.checked);
  };

  return (
    <>
      <div className="flex w-full items-center justify-center">
        <div className="mx-6 md:mx-32 lg:mx-44 xl:mx-64 mt-32 w-full lg:w-2/5">
          <div className="fixed lg:left-20 xl:left-32 mt-2 hidden lg:flex lg:items-center lg:justify-center items-center justify-center w-10 h-10 rounded-full bg-white shadow btn-shadow hover:shadow-s, hover:bg-stone-50 transition-colors">
            <button
              onClick={() => window.history.back()}
              className="w-full h-full rounded-full flex items-center justify-center"
            >
              <ArrowBackIos
                style={{ fontSize: 24, paddingLeft: 6, color: "gray" }}
              />
            </button>
          </div>
          <div className="pt-10 flex flex-col items-center"></div>

          <form onSubmit={handleSubmit} className="pb-8">
            <div className="space-y-3 md:space-y-4">
              <div className="space-y-2">
                <label
                  className="text-base md:text-lg text-gray-500"
                  htmlFor=""
                >
                  Organization Name
                </label>
                <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                  <input
                    type="text"
                    className="outline-none w-full text-base font-medium text-black "
                    name="name"
                    placeholder="Organization Name"
                    ref={nameInputElement}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label
                  className="text-base md:text-lg text-gray-500"
                  htmlFor=""
                >
                  Overtime Calculation Method
                </label>
                <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                  <select
                    className="outline-none w-full text-base font-medium text-black"
                    name="otMethod"
                    ref={otMethodInputElement}
                    defaultValue="default"
                  >
                    <option value="default">Default (Weekly Threshold)</option>
                    <option value="california">
                      California (Daily + Weekly)
                    </option>
                    <option value="australian">
                      Australian (Tiered Weekly)
                    </option>
                  </select>
                </div>
                <p className="text-sm text-gray-500">
                  Default: Overtime after weekly threshold
                  <br />
                  California: 1.5x for daily overtime ({">"}8hrs) + weekly
                  overtime ({">"}40hrs)
                  <br />
                  Australian: 1.5x for 40-42hrs, 2x for 43+hrs
                </p>
              </div>
              <div className="space-y-2">
                <label
                  className="text-base md:text-lg text-gray-500"
                  htmlFor=""
                >
                  Overtime Threshold (hrs)
                </label>
                <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                  <input
                    type="number"
                    className="outline-none w-full text-base font-medium text-black "
                    name="otThreshold"
                    placeholder="40"
                    defaultValue={40}
                    ref={otThresholdInputElement}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label
                  className="text-base md:text-lg text-gray-500"
                  htmlFor=""
                >
                  Default Overtime Multiplier
                </label>
                <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                  <input
                    type="number"
                    className="outline-none w-full text-base font-medium text-black "
                    name="otMultiplier"
                    placeholder="1.5"
                    defaultValue={1.5}
                    ref={otMultiplierInputElement}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label
                  className="text-base md:text-lg text-gray-500"
                  htmlFor=""
                >
                  Salesforce Sync Enabled
                </label>
                <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                  <input
                    type="checkbox"
                    className="outline-none text-base font-medium text-black"
                    name="salesforceSyncEnabled"
                    checked={isSalesforceSyncEnabled}
                    onChange={handleSalesforceSyncChange}
                  />
                </div>
              </div>

              {isSalesforceSyncEnabled && (
                <>
                  <div className="space-y-2">
                    <label
                      className="text-base md:text-lg text-gray-500"
                      htmlFor=""
                    >
                      Salesforce Instance URL
                    </label>
                    <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                      <input
                        type="text"
                        className="outline-none w-full text-base font-medium text-black "
                        name="instanceUrl"
                        placeholder="Instance URL"
                        ref={instanceUrlInputElement}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label
                      className="text-base md:text-lg text-gray-500"
                      htmlFor=""
                    >
                      Salesforce API user's Username
                    </label>
                    <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                      <input
                        type="text"
                        className="outline-none w-full text-base font-medium text-black"
                        name="username"
                        placeholder="Username"
                        ref={usernameInputElement}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label
                      className="text-base md:text-lg text-gray-500"
                      htmlFor=""
                    >
                      Salesforce API User's Password
                    </label>
                    <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                      <input
                        type="password"
                        className="outline-none w-full text-base font-medium text-black"
                        name="password"
                        placeholder="Password"
                        ref={passwordInputElement}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label
                      className="text-base md:text-lg text-gray-500"
                      htmlFor=""
                    >
                      Salesforce API User's Security Token
                    </label>
                    <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                      <input
                        type="text"
                        className="outline-none w-full text-base font-medium text-black"
                        name="securityToken"
                        placeholder="Security Token"
                        ref={securityTokenInputElement}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="pt-4">
                <button className="px-3 py-4 w-full text-white font-medium rounded-xl bg-[#59c3c2] ">
                  {isCreateOrganizationLoading
                    ? "Creating..."
                    : "Create Organization"}
                </button>
              </div>
              {isCreateOrganizationError && (
                <div className="text-sm font-medium text-red-600 pt-2">
                  <p>{createOrganizationError.response.data.error}</p>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
